const CUSTOM_LOCATION_AUTOCOMPLETE_COMPONENT_EVENTS = {
  LOADING_STARTED: 'loading-started',
  LOADING_FINISHED: 'loading-finished',
};

class CustomLocationAutocomplete extends HTMLElement {
  constructor() {
    super();

    this.isResultsContainerVisible = false;
    this.isInitialized = false;
    this.isGoogleApiInitialized = false;
    this.googleAutocompleteService = null;
    this.googlePlacesService = null;
    this.geocoder = null;
    this.googleAutocompleteSessionToken = null;
    this.sessionToken = null;
    this.input = this.querySelector('input[type="text"]');
    this.field = this.querySelector('.field');
    this.resultsContainer = this.querySelector(`.custom-location-autocomplete-field__results`);
    this.focusedResultIndex = -1;
    this.latestSuggestionsRequestIndex = -1;
    this.onInputForSuggestionsFieldListener = null;
    this.onFocusForSuggestionsFieldListener = null;
    this.onBlurForSuggestionsFieldListener = null;
    this.onKeyDownForSuggestionsListener = null;
    this.initGoogle = this.initGoogle.bind(this);
    this.getPlaceForLatLng = this.getPlaceForLatLng.bind(this);
    this.handleSuggestionsError = this.handleSuggestionsError.bind(this);
    this.getPlaceSuggestions = this.getPlaceSuggestions.bind(this);
    this.getGooglePlaceSuggestions = this.getGooglePlaceSuggestions.bind(this);
    this.isValidPlace = this.isValidPlace.bind(this);
    this.onClearInputButtonClick = this.onClearInputButtonClick.bind(this);
    this.getPlaceDetails = this.getPlaceDetails.bind(this);
    this.onResultRowClick = this.onResultRowClick.bind(this);
    this.handlePlaceDetailsError = this.handlePlaceDetailsError.bind(this);
    this.config = {
      debounceTimeout: this.dataset?.configDebounceTimeout ? Number(this.dataset.configDebounceTimeout) : 200,
      minSearchPhraseLength: this.dataset?.configMinSearchPhraseLength ? Number(this.dataset.configMinSearchPhraseLength) : 3,
      showMyLocationOption: this.dataset?.configShowMyLocationOption !== undefined ? this.dataset.configShowMyLocationOption === `true` : true,
      style: {
        resultsContainerMarginTop: this.dataset?.configStyleResultsContainerMarginTop ? Number(this.dataset.configStyleResultsContainerMarginTop) : 0,
        resultsContainerMarginBottom: this.dataset?.configStyleResultsContainerMarginBottom ? Number(this.dataset.configStyleResultsContainerMarginBottom) : 0,
      },
    };

    this.setStyling();
  }

  connectedCallback() {
    if (this.isInitialized) return;

    this.isInitialized = true;

    this.field.querySelector('button.clear').addEventListener('click', this.onClearInputButtonClick);
    this.onInputForSuggestionsField = this.onInputForSuggestionsField.bind(this);

    this.onInputForSuggestionsFieldListener = debounce(this.onInputForSuggestionsField, this.config.debounceTimeout);
    this.input.addEventListener('input', this.onInputForSuggestionsFieldListener);

    this.onFocusForSuggestionsFieldListener = this.onFocusForSuggestions.bind(this)
    this.input.addEventListener('focus', this.onFocusForSuggestionsFieldListener);

    this.onBlurForSuggestionsFieldListener = this.onBlurForSuggestions.bind(this);
    this.input.addEventListener('blur', this.onBlurForSuggestionsFieldListener);

    this.onKeyDownForSuggestionsListener = this.onKeyDownForSuggestions.bind(this)
    window.addEventListener('keydown', this.onKeyDownForSuggestionsListener);

    // Subscribe to bevmoLocationUpdate event to update autocomplete input
    this.onBevmoLocationUpdateListener = this.onBevmoLocationUpdate.bind(this);
    this.bevmoLocationUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.bevmoLocationUpdate, this.onBevmoLocationUpdateListener);
  }

  disconnectedCallback() {
    this.input.removeEventListener('input', this.onInputForSuggestionsFieldListener);
    this.input.removeEventListener('focus', this.onFocusForSuggestionsFieldListener);
    this.input.removeEventListener('blur', this.onBlurForSuggestionsFieldListener);
    window.removeEventListener('keydown', this.onKeyDownForSuggestionsListener);
    
    // Unsubscribe from bevmoLocation updates if subscribed
    if (this.bevmoLocationUpdateUnsubscriber) {
      this.bevmoLocationUpdateUnsubscriber();
      this.bevmoLocationUpdateUnsubscriber = null;
    }
    
    this.isInitialized = false;
  }

  onBevmoLocationUpdate() {
    // Update the input value when bevmoLocation changes externally
    if (window.bevmoLocation?.place?.formatted_address) {
      this.input.value = window.bevmoLocation.place.formatted_address;
    } else if (window.bevmoLocation?.address?.autocomplete) {
      this.input.value = window.bevmoLocation.address.autocomplete;
    }
  }

  async initGoogle() {
    try {
      if (this.isGoogleApiInitialized) return;
      this.isGoogleApiInitialized = true;

      const { AutocompleteService, PlacesService, AutocompleteSessionToken } = await google.maps.importLibrary("places");
      const { Geocoder } = await google.maps.importLibrary("geocoding");

      this.googleAutocompleteSessionToken = AutocompleteSessionToken;
      this.googleAutocompleteService = new AutocompleteService();
      this.googlePlacesService = new PlacesService(document.createElement(`div`));
      this.geocoder = new Geocoder();
    } catch (error) {
      this.isGoogleApiInitialized = false;
      console.error(`Error while initializing Google API: ${error}`);
    }
  }

  getPlaceForLatLng() {
    return new Promise((resolve, reject) => {
      this.geocoder
        .geocode({ location: locationLatLng })
        .then((response) => {
          const place = response.results
            ? response.results[0]
            : null;

          if (!place) {
            return reject();
          }

          const formattedPlace = {};
          const desiredFields = [`formatted_address`, `address_components`, `geometry`, `place_id`];

          desiredFields.forEach((field) => {
            formattedPlace[field] = place[field];
          });

          return resolve(formattedPlace);
        })
        .catch((error) => {
          return reject(error);
        });
    });
  }

  setStyling() {
    this.resultsContainer.style.marginTop = `${this.config.style.resultsContainerMarginTop}px`;
    this.resultsContainer.style.marginBottom = `${this.config.style.resultsContainerMarginBottom}px`;
  }

  clearSessionToken() {
    this.sessionToken = null;
  }

  getSessionToken() {
    this.sessionToken = this.sessionToken || new this.googleAutocompleteSessionToken();
    return this.sessionToken;
  }

  onInputForSuggestionsField(event) {
    if (!this.isGoogleApiInitialized || !this.isInitialized) return;
  }

  handleSuggestionsError() {
    this.toggleResultsVisibility(false);
    this.clearResults();
    this.dispatchEvent(new CustomEvent(CUSTOM_LOCATION_AUTOCOMPLETE_COMPONENT_EVENTS.LOADING_FINISHED, { detail: { success: false } }));
  }

  getGooglePlaceSuggestions(searchPhrase = ``) {
    return new Promise((resolve, reject) => {
      if (!searchPhrase) { return resolve([]); }
      if (window.gopuffShopify.settings.autocomplete_source === `radar`) { return resolve([]); }

      const params = {
        input: searchPhrase,
        sessionToken: this.getSessionToken(),
        componentRestrictions: {
          country: 'US',
        },
      };

      const callback = (suggestions, status) => {
        if (status !== 'OK') { return reject(); }

        return resolve(suggestions);
      }

      this.googleAutocompleteService.getPlacePredictions(params, callback);
    });
  }

  async getPlaceSuggestions(searchPhrase = ``) {
    if (!searchPhrase) { return []; }

    const suggestions = await window.bevmoLocation.getRadarPlaceSuggestions(searchPhrase, 5) || await this.getGooglePlaceSuggestions(searchPhrase);

    return suggestions;
  }

  async onInputForSuggestionsField(event) {
    if (!this.isInitialized) return;

    const searchPhrase = event?.target?.value?.trim() || null;

    if (searchPhrase === null || searchPhrase.length === 0) {
      this.toggleClearInputButtonVisibility(false);
      this.showMyLocationOptionOnly();
      return;
    }

    if (searchPhrase.length < this.config.minSearchPhraseLength) {
      return;
    }

    this.toggleClearInputButtonVisibility(true);
    this.dispatchEvent(new CustomEvent(CUSTOM_LOCATION_AUTOCOMPLETE_COMPONENT_EVENTS.LOADING_STARTED));

    try {
      this.latestSuggestionsRequestIndex += 1;
      const currentIndex = this.latestSuggestionsRequestIndex;
      const suggestions = await this.getPlaceSuggestions(searchPhrase);
      const isStaleRequest = Boolean(currentIndex < this.latestSuggestionsRequestIndex);

      if (isStaleRequest) { return; }

      if (suggestions.length === 0) {
        this.handleSuggestionsError();

        return;
      }

      suggestions.forEach((suggestion, index) => {
        this.addResult(this.getResultRowWithData(suggestion, this.input, this.resultsContainer), index === 0);
      });

      this.toggleResultsVisibility(true);
      this.dispatchEvent(new CustomEvent(CUSTOM_LOCATION_AUTOCOMPLETE_COMPONENT_EVENTS.LOADING_FINISHED, { detail: { success: true } }));
    } catch (error) {
      this.handleSuggestionsError();
    }
  }

  onKeyDownForSuggestions(event) {
    if (!this.isResultsContainerVisible) return;

    if (event.key === `ArrowUp` || event.key === `ArrowDown`) {
      event.preventDefault();

      let prevFocusedResultIndex;

      switch (event.key) {
        case `ArrowUp`:
          this.focusedResultIndex = this.focusedResultIndex - 1 >= 0
            ? this.focusedResultIndex - 1
            : this.resultsContainer.children.length - 1;

          prevFocusedResultIndex = this.focusedResultIndex + 1 < this.resultsContainer.children.length
            ? this.focusedResultIndex + 1
            : 0;
          break;
        case `ArrowDown`:
          this.focusedResultIndex = this.focusedResultIndex + 1 < this.resultsContainer.children.length
            ? this.focusedResultIndex + 1
            : 0;

          prevFocusedResultIndex = this.focusedResultIndex - 1 >= 0
            ? this.focusedResultIndex - 1
            : this.resultsContainer.children.length - 1;
          break;
      }

      /** below is a hack to programmatically focus-visible on contenteditable element */
      this.resultsContainer.children[this.focusedResultIndex].contentEditable = true;
      this.resultsContainer.children[this.focusedResultIndex].focus();
      this.resultsContainer.children[this.focusedResultIndex].contentEditable = false;
    }

    if (event.key === `Enter` && this.focusedResultIndex >= 0) {
      event.preventDefault();
      this.resultsContainer.children[this.focusedResultIndex].click();
    }
  }

  onFocusForSuggestions() {
    if (this.input.value.length === 0) {
      this.showMyLocationOptionOnly();
    }
  }

  onBlurForSuggestions() {
    setTimeout(() => {
      if (this.input.value.length === 0) {
        this.toggleResultsVisibility(false);
      }
    }, 100);
  }

  onClearInputButtonClick(event) {
    event.preventDefault();
    event.stopPropagation();

    this.input.value = '';
    this.clearResults();
    this.toggleClearInputButtonVisibility(false);
    this.input.focus();
  }

  showMyLocationOptionOnly() {
    this.clearResults();
    if (this.config?.showMyLocationOption && navigator.geolocation) {
      this.addResult(this.getMyLocationResultItem(), false);
    }
    this.toggleResultsVisibility(true);
  }

  onResultRowClick(event, data) {
    event.preventDefault();
    event.stopPropagation();
    this.dispatchEvent(new CustomEvent(CUSTOM_LOCATION_AUTOCOMPLETE_COMPONENT_EVENTS.LOADING_STARTED));

    this.getPlaceDetails(data).then(place => {
      this.dispatchEvent(new CustomEvent(CUSTOM_LOCATION_AUTOCOMPLETE_COMPONENT_EVENTS.LOADING_FINISHED, { detail: { success: true } }));
      publish(PUB_SUB_EVENTS.customLocationComponentPlaceChanged, { source: this.id, place });
      this.input.value = place.formatted_address;
      this.clearResults();
    }).catch(this.handlePlaceDetailsError);
  }

  getResultRowWithData(data) {
    const item = document.createElement('a');
    item.classList.add('item');
    item.setAttribute('tabindex', '0');
    item.innerHTML = `<div class="item__icon"><svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg"><use xlink:href="#svg-location-marker"></use></svg></div><div class="item__content"><strong>${data?.structured_formatting?.main_text}</strong><br />${data?.structured_formatting?.secondary_text || ''}</div>`;

    item.addEventListener('click', (event) => this.onResultRowClick(event, data));
    item.addEventListener('keydown', (event) => {
      if (event.key === `Enter`) {
        event.preventDefault();
        event.target.click();
      }
    });

    return item;
  };

  handlePlaceDetailsError(error) {
    console.error(`Error while getting place details: ${error}`);
    this.input.value = ``;
    this.clearResults();
    this.dispatchEvent(new CustomEvent(CUSTOM_LOCATION_AUTOCOMPLETE_COMPONENT_EVENTS.LOADING_FINISHED, { detail: { success: false } }));
  }

  getMyLocationResultItem() {
    const item = document.createElement('a');
    item.classList.add('item');
    item.classList.add('my-location-item');
    item.setAttribute('tabindex', '0');
    item.innerHTML = `<div class="item__icon"><svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg"><use xlink:href="#svg-location-marker"></use></svg></div><div class="item__content"><strong>Get my current location</strong></div>`;

    item.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (navigator.geolocation) {
        this.dispatchEvent(new CustomEvent(CUSTOM_LOCATION_AUTOCOMPLETE_COMPONENT_EVENTS.LOADING_STARTED));

        navigator.geolocation.getCurrentPosition(async (position) => {
          const locationLatLng = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };

          try {
            const place = await this.getPlaceForLatLng({ location: locationLatLng });

            if (place) {
              publish(PUB_SUB_EVENTS.customLocationComponentPlaceChanged, { source: this.id, place });
              this.dispatchEvent(new CustomEvent(CUSTOM_LOCATION_AUTOCOMPLETE_COMPONENT_EVENTS.LOADING_FINISHED, { detail: { success: true } }));
              this.input.value = place.formatted_address;
              this.toggleClearInputButtonVisibility(true);
              this.clearResults();
            }
          } catch (error) {
            console.error(`Error while geocoding user location: ${error}`);
            this.input.value = ``;
            this.clearResults();
            this.toggleClearInputButtonVisibility(false);
            this.dispatchEvent(new CustomEvent(CUSTOM_LOCATION_AUTOCOMPLETE_COMPONENT_EVENTS.LOADING_FINISHED, { detail: { success: false } }));
          }
        });
      }
    });

    return item;
  }

  clearResults() {
    this.resultsContainer.innerHTML = '';
    this.focusedResultIndex = -1;
    this.toggleResultsVisibility(false);
  }

  addResult(item, isFirst) {
    if (isFirst) {
      this.clearResults();
      if (this.config?.showMyLocationOption && navigator.geolocation) {
        this.resultsContainer.append(this.getMyLocationResultItem());
      }
    }

    this.resultsContainer.append(item);
  }

  toggleResultsVisibility(isVisible) {
    this.isResultsContainerVisible = isVisible;
    isVisible
      ? this.resultsContainer.classList.remove('hidden')
      : this.resultsContainer.classList.add('hidden');
  }

  isValidPlace(place = {}) {
    return Boolean(place.geometry && place.address_components && place.formatted_address);
  }

  getPlaceDetails(data = {}) {
    return new Promise((resolve, reject) => {
      if (this.isValidPlace(data)) { return resolve(data); }

      const { place_id: placeId, formatted_address } = data;
      const cachedPlace = bevmoLocation.getCachedPlace(formatted_address, placeId);

      if (cachedPlace) { return resolve(cachedPlace); }
      if (window.gopuffShopify.settings.autocomplete_source === `radar`) { return reject(`autocomplete source is set to radar`); }

      this.googlePlacesService.getDetails(
        {
          placeId,
          sessionToken: this.getSessionToken(),
          fields: [
            'geometry',
            'address_components',
            'formatted_address',
          ]
        },
        (place, status) => {
          this.clearSessionToken();

          if (status !== 'OK') {
            return reject(status);
          }

          return resolve({
            ...place,
            place_id: placeId,
          });
        }
      );
    });
  }

  toggleClearInputButtonVisibility(isVisible) {
    this.field.querySelector('button.clear').style.opacity = isVisible ? '1' : '0';
  }
}
customElements.define('custom-location-autocomplete', CustomLocationAutocomplete);
